import { Modal, Form, Button, Steps } from "antd";
import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import apiClient from "../../../../../helpers/apiClient";
import useCarPricingMode from "../../../../cars/hooks/useCarPricingMode";
import { useUser } from "../../../../../contexts/UserContext/UserContext";
import { useToast } from "../../../../../contexts/ToastContext";
import {
    PAYMENT_METHOD_VALUES,
    buildAdminPaymentOptions,
    getDefaultPaymentMethod,
    registerReservationCashPayment,
} from "../../../lib/reservationPayment";
import confirmDuePaymentFlow from "../../../lib/confirmDuePaymentFlow";
import useSolaCardPayment from "../../../../../components/payment/useSolaCardPayment";
import customizeRequiredMark from "../../../../../components/custom_antd/CustomizeRequiredMark";
import {
    getAlreadyPaidAmount,
    getCarLocationMongoId,
    getReservationPickupLocationId,
} from "../../../../addCoverage/utils/reservationHelpers";
import { buildCarSwapReservationInvoice, computeCarSwapAmountDue } from "./carSwapInvoiceDue";
import SelectCar from "./SelectCar";
import CarSwapInsuranceStep from "./CarSwapInsuranceStep";
import AddonsSelect from "./AddonsSelect";
import InvoiceView from "./InvoiceView";
import CarSwapPaymentStep from "./CarSwapPaymentStep";

const STEP_CAR = 0;
const STEP_INSURANCE = 1;
const STEP_ADDONS = 2;
const STEP_INVOICE = 3;
const STEP_PAYMENT = 4;

const DEFAULT_INSURANCE_SELECTION = {
    insuranceAddonsData: [],
    selectedInsuranceId: null,
    axleTokenData: null,
    isAxleVerifying: false,
    isAxleOptionSelected: false,
    noProtection: false,
};

const getCategoryId = (category) => {
    if (!category) return null;
    if (typeof category === "string") return category;
    if (category._id) return String(category._id);
    return null;
};

const getLocationPublicId = (location) => {
    if (!location) return undefined;
    if (typeof location === "string") return location;
    return location.id ?? location._id;
};

/** Reservation carSelected / carLinked entries must include Mongo _id and public id (UUID). */
const buildSwappedCarRecord = (car) => {
    if (!car?._id || !car?.id) {
        return null;
    }

    return {
        _id: String(car._id),
        id: String(car.id),
        VIN: car.VIN,
        carNumber: car.carNumber,
        carName: car.carName,
        ezPassTagNumber: car.ezPassTagNumber,
        serialNumber: car.serialNumber,
        location: getCarLocationMongoId(car),
    };
};

const CarSwapModal = ({ isOpen, setIsOpen, resDetails, onCarSwapSuccess, setResDetails }) => {
    const { t } = useTranslation(["reservations", "reservationPayment"]);
    const tInsurance = useCallback((key, options) => t(`carSwapModal.${key}`, options), [t]);
    const [form] = Form.useForm();

    const toast = useToast();
    const { myCompanyData: companyData } = useUser();
    const isCarPricingMode = useCarPricingMode();
    const { requestSolaCardPayment, solaCardModal } = useSolaCardPayment();

    // store data states
    const [availableCars, setAvailableCars] = useState([]);
    const [exisitingAddons, setExistingAddons] = useState([]);
    const [availabeAddons, setAvailableAddons] = useState([]);
    const [selectedAddons, setSelectedAddons] = useState([]);
    const [invoice, setInvoice] = useState(null);

    // State to track selected car ID to trigger addon fetching
    const [selectedCarId, setSelectedCarId] = useState(null);
    const [insuranceSelection, setInsuranceSelection] = useState(DEFAULT_INSURANCE_SELECTION);

    // interaction management states
    const [isLoading, setIsLoading] = useState(false);
    const [isAddonsLoading, setIsAddonsLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [step, setStep] = useState(0);
    const [paymentSystem, setPaymentSystem] = useState(null);

    const defaultPaymentGateway = companyData?.defaultPaymentGateway || "stripe";
    const showCashPayment = companyData?.preferences?.showCashPayment === true;
    const paymentOptions = useMemo(
        () =>
            buildAdminPaymentOptions({
                defaultPaymentGateway,
                showCashPayment,
                t,
            }),
        [defaultPaymentGateway, showCashPayment, t],
    );

    const swapDueAmount = useMemo(() => Number(invoice?.due ?? 0), [invoice?.due]);
    const requiresPayment = swapDueAmount > 0;

    const stepItems = useMemo(() => {
        const items = [
            { title: t("carSwapModal.carStep") },
            { title: t("carSwapModal.insuranceStep") },
            { title: t("carSwapModal.addonsStep") },
            { title: t("carSwapModal.invoiceStep") },
        ];
        if (requiresPayment) {
            items.push({ title: t("carSwapModal.paymentStep") });
        }
        return items;
    }, [requiresPayment, t]);

    // The car actually selected in step 0 — used to resolve insurance availability
    // and to build the swapped-car record on submit.
    const selectedCarInfo = useMemo(
        () =>
            availableCars.find(
                (car) => String(car._id) === String(selectedCarId) || car.id === selectedCarId,
            ),
        [availableCars, selectedCarId],
    );

    const insurancePickupLocationId = useMemo(
        () =>
            getReservationPickupLocationId({
                carSelected: selectedCarInfo,
                pickupLocation: resDetails?.pickupLocation,
            }),
        [selectedCarInfo, resDetails?.pickupLocation],
    );

    // Gate the insurance step: the admin must either pick a plan, finish Axle
    // verification, or explicitly continue without protection.
    const canProceedInsurance = useMemo(() => {
        if (insuranceSelection.isAxleVerifying) return false;
        if (insuranceSelection.axleTokenData) return true;
        if (insuranceSelection.isAxleOptionSelected) return false;
        if (insuranceSelection.noProtection) return true;
        return Boolean(insuranceSelection.selectedInsuranceId);
    }, [insuranceSelection]);

    // Derive unique categories from the available-cars response and always include
    // the reservation's current category so the default Select value can resolve to a label.
    const derivedCategories = useMemo(() => {
        const categoryMap = new Map();

        availableCars.forEach((car) => {
            const categoryId = getCategoryId(car.category);
            if (!categoryId || categoryMap.has(categoryId)) return;

            categoryMap.set(categoryId, {
                ...(typeof car.category === "object" ? car.category : {}),
                _id: categoryId,
                categoryName: car.category?.categoryName || car.bodyType || categoryId,
            });
        });

        const reservationCategoryId = getCategoryId(resDetails?.category);
        if (reservationCategoryId && !categoryMap.has(reservationCategoryId)) {
            categoryMap.set(reservationCategoryId, {
                ...(typeof resDetails?.category === "object" ? resDetails.category : {}),
                _id: reservationCategoryId,
                categoryName:
                    (typeof resDetails?.category === "object" && resDetails.category?.categoryName) ||
                    resDetails?.carType ||
                    reservationCategoryId,
            });
        }

        return Array.from(categoryMap.values());
    }, [availableCars, resDetails?.category, resDetails?.carType]);

    // This function resets the modal state and form fields
    const handleCancel = () => {
        setIsOpen(false);
        setStep(0);
        setSubmitLoading(false);
        setAvailableCars([]);
        setExistingAddons([]);
        setAvailableAddons([]);
        setSelectedAddons([]);
        setSelectedCarId(null);
        setIsLoading(false);
        setIsAddonsLoading(false);
        setInvoice(null);
        setPaymentSystem(null);
        setInsuranceSelection(DEFAULT_INSURANCE_SELECTION);
        form.resetFields();
    };

    // Handle car selection change
    const handleCarChange = (carId) => {
        setSelectedCarId(carId);
    };

    // This function handles the next step in the modal
    const handleNextStep = async () => {
        try {
            // Validate current step form fields
            if (step === 0) {
                await form.validateFields(["category", "car"]);
            } else if (step === STEP_INSURANCE) {
                if (!canProceedInsurance) {
                    toast.error({ message: t("carSwapModal.selectInsuranceRequired") });
                    return;
                }
            } else if (step === STEP_INVOICE) {
                if (invoiceLoading || !invoice) {
                    return;
                }
                if (requiresPayment) {
                    setStep(STEP_PAYMENT);
                    return;
                }
            }

            setStep((prev) => prev + 1);
        } catch (_error) {
            // Validation failed, form will show inline errors
            // No need for additional handling
        }
    };

    // Fetch all available cars once; response structure varies by pricingMode.
    const getAvailableCars = useCallback(
        async () => {
            if (!resDetails) return;

            setIsLoading(true);
            setAvailableCars([]);

            try {
                const { data: carsResponse } = await apiClient.get("/api/car/available-cars", {
                    params: {
                        pickupDate: new Date(resDetails.pickupDate).toISOString(),
                        dropoffDate: new Date(resDetails.dropoffDate).toISOString(),
                        location: resDetails.pickupLocation.id,
                    },
                });

                const pricingMode = carsResponse?.data?.pricingMode;
                const results = carsResponse?.data?.results || [];

                // category mode: results are category objects, each containing a `cars` array
                // car mode: results are flat car objects
                setAvailableCars(
                    pricingMode === "category"
                        ? results.flatMap((cat) =>
                              (cat.cars || []).map((car) => ({
                                  ...car,
                                  category: car.category || cat,
                              })),
                          )
                        : results,
                );
            } catch (_error) {
                // console.error("Error fetching available cars:", error);
            } finally {
                setIsLoading(false);
            }
        },
        [resDetails],
    );

    // Fetch all available cars when the modal opens.
    useEffect(() => {
        if (isOpen && resDetails) {
            getAvailableCars();
        }
    }, [isOpen, resDetails, getAvailableCars]);

    // Pre-select the reservation's current category AFTER derivedCategories is populated,
    // so Ant Design Select can resolve the label from its options list.
    useEffect(() => {
        if (!isOpen || derivedCategories.length === 0) return;
        const reservationCategoryId = getCategoryId(resDetails?.category);
        if (reservationCategoryId) {
            form.setFieldsValue({ category: reservationCategoryId });
        }
    }, [isOpen, derivedCategories, resDetails?.category, form]);

    // fetch available cars addons and validate existing addons with newly selected car
    useEffect(() => {
        if (!isOpen || !resDetails || !selectedCarId) return;

        (async () => {
            setIsAddonsLoading(true);
            try {
                // Fetch addons for the exisitng car
                const existingAddons = resDetails.addons || [];
                // const existingAddonsDetails = [];

                if (existingAddons.length > 0) {
                    const addonPromises = existingAddons.map(async (addonsId) => {
                        const { data: addonDetails } = await apiClient.get(`/api/addon/${addonsId}`);

                        if (addonDetails?.data) {
                            const isAvailableForNewCar =
                                addonDetails.data?.cars?.length === 0 ||
                                addonDetails.data?.cars?.some((car) => car.id === selectedCarId);

                            return {
                                ...addonDetails.data,
                                isAvailableForNewCar,
                            };
                        }
                        return null;
                    });

                    const resolvedAddons = await Promise.all(addonPromises);
                    const validAddons = resolvedAddons.filter(Boolean);
                    setExistingAddons(validAddons);
                }

                // Fetch all availabe addons for the newly selected car
                const params = {
                    cars: selectedCarId,
                    type: "regular",
                    status: "active",
                    page: 1,
                    limit: 1000,
                };

                const { data: addonsResponse } = await apiClient.get("/api/addon/available", { params });

                if (addonsResponse?.data && addonsResponse.data?.result?.length) {
                    const addons = addonsResponse.data?.result;
                    const availableAddons = addons.filter(
                        (addon) => !existingAddons.some((existingAddon) => existingAddon === addon.id),
                    );
                    // availableAddons = availableAddons.filter(
                    //     addon => !selectedAddons.some(selectedAddon => selectedAddon === addon.id)
                    // );

                    setAvailableAddons(availableAddons);
                }
            } catch (_error) {
                // console.error("Error fetching car addons:", error);
            } finally {
                setIsAddonsLoading(false);
            }
        })();
    }, [isOpen, resDetails, selectedCarId]);

    // Generate invoice details when reservation details change
    const generateInvoiceDetails = useCallback(async () => {
        try {
            setInvoiceLoading(true);

            const addonIds = [
                ...selectedAddons.map((addon) => addon.id),
                ...exisitingAddons.filter((addon) => addon.isAvailableForNewCar === true).map((addon) => addon.id),
            ];

            const { data: invoiceDetails } = await apiClient.post("/api/reservation/invoice-v2", {
                carId: selectedCarId,
                category: form.getFieldValue("category"),
                pickupDate: new Date(resDetails.pickupDate).toISOString(),
                dropoffDate: new Date(resDetails.dropoffDate).toISOString(),
                location: resDetails.pickupLocation?.id,
                addonIdList: addonIds,
                // insuranceId must be omitted (not sent as null) — Zod .optional() rejects null.
                // Axle ("own insurance") and "continue without protection" both omit it, since
                // neither adds an invoice line item.
                ...(insuranceSelection.selectedInsuranceId && !insuranceSelection.axleTokenData
                    ? { insuranceId: insuranceSelection.selectedInsuranceId }
                    : {}),
            });

            const nextInvoice = invoiceDetails?.data?.invoice || null;
            if (nextInvoice) {
                const alreadyPaid = getAlreadyPaidAmount(resDetails);
                const total = Number(nextInvoice.total || 0);
                const due = computeCarSwapAmountDue(total, alreadyPaid);

                setInvoice({ ...nextInvoice, alreadyPaid, due });
            } else {
                setInvoice(null);
            }
            setInvoiceLoading(false);
        } catch (_error) {
            // console.error("Error generating invoice details:", error);
            setInvoiceLoading(false);
        }
    }, [
        exisitingAddons,
        form,
        insuranceSelection.axleTokenData,
        insuranceSelection.selectedInsuranceId,
        resDetails,
        selectedAddons,
        selectedCarId,
    ]);

    useEffect(() => {
        if (step !== STEP_PAYMENT || !requiresPayment) {
            setPaymentSystem(null);
            return;
        }
        if (!paymentSystem && paymentOptions.length > 0) {
            setPaymentSystem(getDefaultPaymentMethod(paymentOptions));
        }
    }, [paymentOptions, paymentSystem, requiresPayment, step]);

    const getFormCarValues = () => ({
        category: form.getFieldValue("category"),
        car: form.getFieldValue("car"),
    });

    const runWithCategoryConfirm = (values, onConfirmed) => {
        if (!isCarPricingMode) {
            const selectedCategoryInfo = derivedCategories.find((cat) => getCategoryId(cat) === values.category);
            const resCategoryId =
                typeof resDetails?.category === "object"
                    ? getCategoryId(resDetails.category)
                    : getCategoryId(resDetails?.category);

            const isCategoryDifferent =
                selectedCategoryInfo &&
                resCategoryId &&
                getCategoryId(selectedCategoryInfo) !== resCategoryId;

            if (isCategoryDifferent === undefined) {
                toast.error({
                    message: t("carSwapModal.errorForCategory"),
                });
                return;
            }

            if (isCategoryDifferent) {
                Modal.confirm({
                    title: t("carSwapModal.confirmTitle"),
                    content: t("carSwapModal.confirmMessage", {
                        categoryName1: resDetails?.category?.categoryName || resDetails?.carType,
                        categoryName2: selectedCategoryInfo.categoryName,
                    }),
                    okText: t("carSwapModal.yes"),
                    okType: "primary",
                    cancelText: t("carSwapModal.no"),
                    onOk: () => onConfirmed(values),
                    centered: true,
                });
                return;
            }
        }

        onConfirmed(values);
    };

    // This function processes the car swap or addition
    const processCarSwap = async (values, { additionalCashPaid = 0, skipPageReload = false } = {}) => {
        try {
            setSubmitLoading(true);

            const swapInvoice = buildCarSwapReservationInvoice({
                quoteInvoice: invoice,
                resDetails,
                additionalPaid: additionalCashPaid,
            });

            const carSelected = buildSwappedCarRecord(selectedCarInfo);
            if (!carSelected) {
                toast.error({
                    message: t("carSwapModal.errorForSwap"),
                    description: t("carSwapModal.carSelectRequired"),
                });
                setSubmitLoading(false);
                return undefined;
            }

            const selectedCategoryInfo = derivedCategories.find((cat) => getCategoryId(cat) === values.category);

            const pickupLocation =
                getLocationPublicId(selectedCarInfo?.location) ??
                (typeof resDetails?.pickupLocation === "string"
                    ? resDetails.pickupLocation
                    : resDetails?.pickupLocation?.id ?? resDetails?.pickupLocation?._id);

            const addonIds = [
                ...selectedAddons.map((addon) => addon.id),
                ...exisitingAddons.filter((addon) => addon.isAvailableForNewCar === true).map((addon) => addon.id),
            ];

            // Persist the choice made on the "Select insurance" step onto the reservation.
            // The step always requires an explicit choice, so this always REPLACES whatever
            // insurance the reservation previously had:
            //  - a newly picked addon/Axle policy replaces the prior insurance (even a
            //    different provider/type), and
            //  - "Continue without protection" explicitly REMOVES any prior coverage — this is
            //    the expected outcome when the swapped-in car has no matching insurance option.
            const selectedInsuranceAddon = insuranceSelection.insuranceAddonsData.find(
                (addon) => addon.id === insuranceSelection.selectedInsuranceId,
            );
            const insuranceFields = insuranceSelection.axleTokenData
                ? {
                      insuranceType: "axle",
                      addonsInsuranceId: null,
                      axleInsurance: {
                          accessToken: insuranceSelection.axleTokenData.accessToken,
                          policyId: insuranceSelection.axleTokenData.policies?.[0],
                      },
                  }
                : insuranceSelection.selectedInsuranceId
                  ? {
                        insuranceType: selectedInsuranceAddon?.provider || "teqMobility",
                        addonsInsuranceId: insuranceSelection.selectedInsuranceId,
                        axleInsurance: null,
                    }
                  : {
                        // "Continue without protection" — clear any previously selected insurance.
                        insuranceType: null,
                        addonsInsuranceId: null,
                        axleInsurance: null,
                    };

            // merge car default and invoice details with reservation details
            const newReservationDetails = {
                ...resDetails,
                carName: selectedCarInfo?.carName,
                carType: selectedCategoryInfo?.categoryName || selectedCarInfo?.bodyType,
                category: getCategoryId(selectedCategoryInfo) || getCategoryId(resDetails?.category),
                carSelected,
                carLinked: [{ ...carSelected }],
                invoice: swapInvoice,
                addons: addonIds,
                pickupLocation,
                ...insuranceFields,
            };

            //  update reservation with new car details
            await apiClient.patch("/api/reservation/link-and-unlink-car", {
                reservationIds: [resDetails.id],
                reservationInfo: newReservationDetails,
            });

            const { data } = await apiClient.get(`/api/reservation/${resDetails.id}`);

            const resDetail = data?.data;
            setResDetails(resDetail);

            if (!skipPageReload) {
                toast.success({
                    message: resDetails?.carSelected
                        ? t("carSwapModal.successForSwap")
                        : t("carSwapModal.successForAdd"),
                });
            }

            if (onCarSwapSuccess) {
                onCarSwapSuccess(newReservationDetails);
            }

            setSubmitLoading(false);

            if (skipPageReload) {
                return resDetail;
            }

            handleCancel();
            window.location.reload();
            return resDetail;
        } catch (error) {
            // console.log("Error swapping car:", error);

            if (error.errorFields) {
                // This is a validation error, no need to show a toast
                // console.log("Validation failed:", error);
            } else {
                const errorMessage = resDetails?.carSelected
                    ? t("carSwapModal.errorForSwap")
                    : t("carSwapModal.errorForAdd");
                toast.error({
                    message: errorMessage,
                });
            }
            setSubmitLoading(false);
            return undefined;
        }
    };

    const handlePaymentAndSwap = async () => {
        if (!paymentSystem) {
            toast.error({ message: t("carSwapModal.selectPaymentRequired") });
            return;
        }

        try {
            await form.validateFields(["category", "car"]);
        } catch {
            return;
        }

        const values = getFormCarValues();
        const dueAmount = swapDueAmount;

        runWithCategoryConfirm(values, async (confirmedValues) => {
            try {
                setSubmitLoading(true);

                if (paymentSystem === PAYMENT_METHOD_VALUES.CASH) {
                    await registerReservationCashPayment(resDetails.id, dueAmount, "scheduled");
                    await processCarSwap(confirmedValues, { additionalCashPaid: dueAmount });
                    return;
                }

                const updatedReservation = await processCarSwap(confirmedValues, { skipPageReload: true });
                const paymentResult = await confirmDuePaymentFlow({
                    reservation: updatedReservation || resDetails,
                    dueAmount,
                    paymentSystem,
                    companyData,
                    toast,
                    t,
                    requestSolaCardPayment,
                });

                if (paymentResult?.type === "redirect" || paymentResult?.type === "cancelled") {
                    return;
                }

                handleCancel();
                window.location.reload();
            } catch (error) {
                if (error?.errorFields) {
                    return;
                }
                toast.error({
                    message: resDetails?.carSelected
                        ? t("carSwapModal.errorForSwap")
                        : t("carSwapModal.errorForAdd"),
                    description: error?.response?.data?.message || error?.message,
                });
                setSubmitLoading(false);
            }
        });
    };

    // This function handles form submission
    const handleSubmit = async () => {
        try {
            await form.validateFields(["category", "car"]);
            const values = getFormCarValues();

            runWithCategoryConfirm(values, (confirmedValues) => processCarSwap(confirmedValues));
        } catch (_error) {
            // Validation failed, form will show inline errors
        }
    };

    const isInvoiceLastStepWithoutPayment = step === STEP_INVOICE && !requiresPayment;
    const isPaymentStep = step === STEP_PAYMENT && requiresPayment;

    return (
        <>
        <Modal
            open={isOpen}
            title={null}
            onCancel={handleCancel}
            maskClosable={false}
            // closable={false}
            footer={null}
            width={{ xs: "calc(100vw - 32px)", lg: 800 }}
            centered
            style={{ maxWidth: "100vw" }}
            classNames={{
                wrapper: "!p-4 lg:!p-0",
                content: "!max-h-[calc(100dvh-2rem)] !flex !flex-col !overflow-hidden lg:!max-h-[95vh]",
                header: "!mb-0 !p-0",
                body: "!flex !min-h-0 !flex-1 !flex-col !overflow-hidden !p-4 lg:!p-6",
            }}>
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                <div className="mb-4 flex-shrink-0 sm:mb-6">
                    <h2 className="mb-2 text-xl font-bold sm:text-2xl">
                        {resDetails?.carSelected ? t("carSwapModal.title1") : t("carSwapModal.title2")}
                    </h2>
                    <p className="text-base text-gray-500">{t("carSwapModal.description")}</p>
                </div>

                <Steps size="small" current={step} items={stepItems} className="mb-4 flex-shrink-0" />

                <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        requiredMark={customizeRequiredMark}
                        className="flex min-h-0 min-w-0 flex-1 flex-col">
                        <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain">
                            {/* Step 0: Car Selection */}
                            {step === STEP_CAR && (
                                <SelectCar
                                    form={form}
                                    categories={derivedCategories}
                                    availableCars={availableCars}
                                    selectedCarId={selectedCarId}
                                    isLoading={isLoading}
                                    isCategoriesLoading={isLoading}
                                    onCarChange={handleCarChange}
                                />
                            )}

                            {/* Step 1: Select Insurance */}
                            {step === STEP_INSURANCE && (
                                <CarSwapInsuranceStep
                                    carId={selectedCarId}
                                    pickupLocationId={insurancePickupLocationId}
                                    reservation={resDetails}
                                    onSelectionStateChange={setInsuranceSelection}
                                    t={tInsurance}
                                />
                            )}

                            {/* Step 2: Addons Selection */}
                            {step === STEP_ADDONS && (
                                <div className="h-full">
                                    <AddonsSelect
                                        availabeAddons={availabeAddons}
                                        setAvailableAddons={setAvailableAddons}
                                        exisitingAddons={exisitingAddons}
                                        setExistingAddons={setExistingAddons}
                                        selectedAddons={selectedAddons}
                                        setSelectedAddons={setSelectedAddons}
                                        isAddonsLoading={isAddonsLoading}
                                    />
                                </div>
                            )}

                            {/* Step 3: Invoice Details */}
                            {step === STEP_INVOICE && (
                                <InvoiceView
                                    handleInvoiceGenerate={generateInvoiceDetails}
                                    invoice={invoice}
                                    resDetails={resDetails}
                                    invoiceLoading={invoiceLoading}
                                />
                            )}

                            {isPaymentStep ? (
                                <CarSwapPaymentStep
                                    dueAmount={swapDueAmount}
                                    paymentOptions={paymentOptions}
                                    paymentSystem={paymentSystem}
                                    onPaymentChange={setPaymentSystem}
                                    t={t}
                                />
                            ) : null}
                        </div>

                        {/* buttons  */}
                        <div className="mt-4 w-full min-w-0 flex-shrink-0 border-t border-[#f0f0f0] pt-4">
                            <div className="flex w-full min-w-0 items-center justify-end gap-2">
                                {step === STEP_CAR ? (
                                    <Button onClick={handleCancel} shape="round" className="!min-w-0 shrink">
                                        {t("carSwapModal.cancel")}
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={() => setStep((prev) => prev - 1)}
                                        shape="round"
                                        className="!min-w-0 shrink">
                                        {t("carSwapModal.back")}
                                    </Button>
                                )}
                                {isPaymentStep ? (
                                    <Button
                                        type="primary"
                                        shape="round"
                                        loading={submitLoading}
                                        onClick={handlePaymentAndSwap}
                                        disabled={submitLoading}
                                        className="!min-w-0 shrink">
                                        {submitLoading
                                            ? t("carSwapModal.payingAndSwapping")
                                            : t("carSwapModal.payAndSwap")}
                                    </Button>
                                ) : isInvoiceLastStepWithoutPayment ? (
                                    <Button
                                        type="primary"
                                        shape="round"
                                        loading={submitLoading}
                                        onClick={() => form.submit()}
                                        disabled={invoiceLoading || submitLoading}
                                        className="!min-w-0 shrink">
                                        {resDetails?.carSelected
                                            ? submitLoading
                                                ? t("carSwapModal.swappingCar")
                                                : t("carSwapModal.swapCar")
                                            : submitLoading
                                              ? t("carSwapModal.addingCar")
                                              : t("carSwapModal.addCar")}
                                    </Button>
                                ) : step === STEP_INVOICE && requiresPayment ? (
                                    <Button
                                        type="primary"
                                        onClick={handleNextStep}
                                        shape="round"
                                        htmlType="button"
                                        disabled={invoiceLoading || !invoice}
                                        className="!min-w-0 shrink">
                                        {t("carSwapModal.next")}
                                    </Button>
                                ) : (
                                    <Button
                                        type="primary"
                                        onClick={handleNextStep}
                                        shape="round"
                                        htmlType="button"
                                        disabled={step === STEP_INSURANCE && !canProceedInsurance}
                                        className="!min-w-0 shrink">
                                        {t("carSwapModal.next")}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Form>
                </div>
            </div>
        </Modal>
        {solaCardModal}
        </>
    );
};

CarSwapModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    resDetails: PropTypes.object.isRequired,
    onCarSwapSuccess: PropTypes.func,
    setResDetails: PropTypes.func,
};

export default CarSwapModal;
